import { useEffect, useRef, useState } from 'react';

export interface MatchTimeData {
    minutes_played: number;
    seconds_played: number;
    is_running: boolean;
    home_score?: number;
    away_score?: number;
    isEnded?: boolean;
    isFullTime?: boolean;
    isHalfTime?: boolean;
    isExtraTimeFirstHalf?: boolean;
    isExtraTimeSecondHalf?: boolean;
    isHalfTimeOfExtraTime?: boolean;
    isPenalties?: boolean;
    isPreMatch?: boolean;
    isEndOfSecondHalf?: boolean;
    isEndOfExtraTime?: boolean;
    minute?: number;
    time_start?: string;
    currentPeriod?: number;
}

interface UseMatchTimeProps {
    matchClockData: MatchTimeData | null;
    isEnabled?: boolean;
}

interface MatchTimeState {
    minutes: number;
    seconds: number;
    isRunning: boolean;
    lastApiUpdate: number;
    isFullTime: boolean;
    isHalfTime: boolean;
    isExtraTimeFirstHalf: boolean;
    isExtraTimeSecondHalf: boolean;
    isHalfTimeOfExtraTime: boolean;
    isPenalties: boolean;
    isPreMatch: boolean;
    isEndOfSecondHalf: boolean;
    isEndOfExtraTime: boolean;
    minute: number;
    time_start: string;
    isEnded: boolean;
    currentPeriod: number;
}

export const useMatchTime = ({ matchClockData, isEnabled = true }: UseMatchTimeProps) => {
    const [matchTime, setMatchTime] = useState<MatchTimeState>({
        minutes: 0,
        seconds: 0,
        isRunning: false,
        lastApiUpdate: 0,
        isFullTime: false,
        isHalfTime: false,
        isExtraTimeFirstHalf: false,
        isExtraTimeSecondHalf: false,
        isHalfTimeOfExtraTime: false,
        isPenalties: false,
        isPreMatch: false,
        isEndOfSecondHalf: false,
        isEndOfExtraTime: false,
        minute: 0,
        time_start: '',
        isEnded: false,
        currentPeriod: 0
    });

    const intervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastApiTimeRef = useRef<string>('');

    const getTimeString = (minutes: number, seconds: number): string => {
        const safeMinutes = minutes ?? 0;
        const safeSeconds = seconds ?? 0;
        return `${safeMinutes}:${safeSeconds.toString().padStart(2, '0')}`;
    };

    const updateMatchTime = () => {
        setMatchTime(prev => {
            if (!prev.isRunning) return prev;
            
            const newSeconds = prev.seconds + 1;
            const newMinutes = prev.minutes + Math.floor(newSeconds / 60);
            const finalSeconds = newSeconds % 60;
            
            return {
                ...prev,
                minutes: newMinutes,
                seconds: finalSeconds
            };
        });
    };

    useEffect(() => {
        if (!matchClockData || !isEnabled) return;

        const safeMinutes = matchClockData.minutes_played ?? 0;
        const safeSeconds = matchClockData.seconds_played ?? 0;
        const currentApiTime = getTimeString(safeMinutes, safeSeconds);
        const currentApiTimeString = `${safeMinutes}:${safeSeconds.toString().padStart(2, '0')}`;
        
        if (currentApiTimeString !== lastApiTimeRef.current) {
            // Stop previous interval
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }

            // Set new time from API
            setMatchTime({
                minutes: safeMinutes,
                seconds: safeSeconds,
                isRunning: matchClockData.is_running ?? false,
                lastApiUpdate: Date.now(),
                isFullTime: matchClockData.isFullTime ?? false,
                isHalfTime: matchClockData.isHalfTime ?? false,
                isExtraTimeFirstHalf: matchClockData.isExtraTimeFirstHalf ?? false,
                isExtraTimeSecondHalf: matchClockData.isExtraTimeSecondHalf ?? false,
                isHalfTimeOfExtraTime: matchClockData.isHalfTimeOfExtraTime ?? false,
                isPenalties: matchClockData.isPenalties ?? false,
                isPreMatch: matchClockData.isPreMatch ?? false,
                isEndOfSecondHalf: matchClockData.isEndOfSecondHalf ?? false,
                isEndOfExtraTime: matchClockData.isEndOfExtraTime ?? false,
                minute: matchClockData.minute ?? 0,
                time_start: matchClockData.time_start ?? '',
                isEnded: matchClockData.isEnded ?? false,
                currentPeriod: matchClockData.currentPeriod ?? 0
            });

            lastApiTimeRef.current = currentApiTimeString;

            // Run new interval only if the match is in progress
            if (matchClockData.is_running) {
                intervalRef.current = setInterval(updateMatchTime, 1000);
            }
        } else {
            // If the time from API has not changed, but the status is_running has changed
            if (matchTime.isRunning !== matchClockData.is_running) {
                setMatchTime(prev => ({
                    ...prev,
                    isRunning: matchClockData.is_running
                }));

                if (matchClockData.is_running && !intervalRef.current) {
                    // Run interval if the match was resumed
                    intervalRef.current = setInterval(updateMatchTime, 1000);
                } else if (!matchClockData.is_running && intervalRef.current) {
                    // Stop interval if the match was stopped
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                }
            }
        }
    }, [matchClockData, isEnabled]);

    // Cleanup when unmounting
    useEffect(() => {
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);

    return {
        minutes: matchTime.minutes,
        seconds: matchTime.seconds,
        isRunning: matchTime.isRunning,
        formattedTime: matchClockData?.isEnded ? null : getTimeString(matchTime.minutes, matchTime.seconds),
        isFullTime: matchTime.isFullTime,
        isHalfTime: matchTime.isHalfTime,
        isExtraTimeFirstHalf: matchTime.isExtraTimeFirstHalf,
        isExtraTimeSecondHalf: matchTime.isExtraTimeSecondHalf,
        isHalfTimeOfExtraTime: matchTime.isHalfTimeOfExtraTime,
        isPenalties: matchTime.isPenalties,
        isPreMatch: matchTime.isPreMatch,
        isEndOfSecondHalf: matchTime.isEndOfSecondHalf,
        isEndOfExtraTime: matchTime.isEndOfExtraTime,
        minute: matchTime.minute,
        time_start: matchTime.time_start,
        isEnded: matchTime.isEnded,
        currentPeriod: matchTime.currentPeriod,
        lastApiUpdate: matchTime.lastApiUpdate
    };
};
